import {Component} from 'preact'
import {isEqual} from 'underscore'

export interface StateTrackerState {
  hasChanged: boolean
}

export default abstract class StateTracker<P, S extends StateTrackerState, K extends keyof S = keyof S> extends Component<P, S> {
  abstract initialState: Pick<S, K>
  currentState: Pick<S, K> | null
  alertOnExit: boolean = true

  constructor () {
    super()
    this.currentState = null
    this.onBeforeUnload = this.onBeforeUnload.bind(this)
  }

  restoreToInitial (): void {
    this.currentState = this.initialState
    this.setState(this.initialState)
    this.setState({hasChanged: false})
  }

  protected hasChanged () {
    return !isEqual(this.currentState, this.initialState)
  }

  trackStateChange (newState: Pick<S, K>) {
    this.currentState = newState
    this.setState(newState)
    this.setState({
      hasChanged: this.hasChanged()
    })
  }

  onBeforeUnload (e: BeforeUnloadEvent) {
    if (!this.hasChanged()) return
    e.preventDefault()
    e.returnValue = ''
  }

  registerAlert () {
    if (this.alertOnExit) {
      window.addEventListener('beforeunload', this.onBeforeUnload)
    }
  }

  unRegisterAlert () {
    if (this.alertOnExit) {
      window.removeEventListener('beforeunload', this.onBeforeUnload)
    }
  }

  componentDidMount () {
    this.registerAlert()
  }

  componentWillUnmount () {
    this.unRegisterAlert()
  }

  setInitialState (state: Pick<S, K>) {
    this.initialState = state
    this.currentState = state
    this.setState({ hasChanged: false })
  }
}
